import { useRef } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

/**
 * ProductSlideTransition - direction-aware horizontal slide for the
 * "Six products. One compliance posture." carousel (#products / .fw-panel).
 *
 * ── What was already there ────────────────────────────────────────────────
 * This switch was NOT an instant swap. The slides are stacked in one grid cell
 * (grid-area: 1/1) and .fw-slide already carries
 *   opacity 0 -> 1, visibility hidden -> visible,
 *   transform: translateY(10px) scale(0.995) -> none,
 *   transition: opacity .45s ease, transform .45s ease, visibility .45s
 * so switching already cross-faded over 450ms with a slight rise. The comment
 * in FrameworksCarousel.jsx says as much: "the fade/rise lives entirely in CSS".
 *
 * What this changes is the AXIS and the DIRECTION: a vertical rise reads as a
 * card replacing itself in place, where a carousel should read as travel. Now
 * the outgoing slide leaves the way you came from and the incoming one arrives
 * from the side you are heading to, which the CSS could not express because it
 * has no idea which arrow was pressed.
 *
 * ── Why it hooks the class, not the arrows ────────────────────────────────
 * FrameworksCarousel.jsx toggles .is-active from FIVE entry points: the two
 * arrows, the tab strip, arrow-key handling on .fw-tabs, and an autoplay timer.
 * Re-wiring the arrows would animate two of those and leave the rest snapping.
 * A MutationObserver on the deck's class attributes catches every path with one
 * hook and needs no change to that file.
 *
 * ── Why the CSS transition has to be switched off ─────────────────────────
 * GSAP writes inline opacity/transform, which beats the class rules - but the
 * `transition` shorthand would still apply to those inline changes, so every
 * tween would be eased twice (once by GSAP, once by CSS) and land late. The
 * [data-pb-fw-slide] gate zeroes the transition and nothing else.
 *
 * visibility stays under GSAP via autoAlpha, deliberately: the original uses
 * visibility:hidden so links inside off-screen slides are not focusable, and
 * forcing them all visible would make six slides' worth of links tabbable at
 * once. autoAlpha preserves that - it sets visibility:hidden at opacity 0.
 */

const DURATION = 0.5
const EASE = 'power2.out'
const DIST = 48   // px of travel; the deck is one grid cell, so this is a hint of motion, not a full slide-over

const GATE = 'data-pb-fw-slide'
const GATE_CSS = `
#page-home #products .fw-panel[${GATE}] .fw-slide { transition: none; }
`
if (typeof document !== 'undefined' && !document.getElementById('pb-fw-slide-css')) {
  const el = document.createElement('style')
  el.id = 'pb-fw-slide-css'
  el.textContent = GATE_CSS
  document.head.appendChild(el)
}

export default function ProductSlideTransition() {
  const done = useRef(false)

  useGSAP(() => {
    if (done.current) return
    if (window.location.pathname !== '/') return

    let finder = null
    let classWatcher = null
    let timeoutId = 0

    const attach = () => {
      const panel = document.querySelector('#page-home #products .fw-panel')
      const deck = panel && panel.querySelector('.fw-deck')
      if (!panel || !deck) return false

      const slides = Array.from(deck.querySelectorAll('.fw-slide'))
      if (slides.length < 2) return false

      done.current = true

      const mm = gsap.matchMedia()

      // Under `reduce` the gate is never installed, so the original CSS - which
      // has its own prefers-reduced-motion branch dropping the transform and
      // the transition - stays in charge. Nothing to undo.
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        panel.setAttribute(GATE, '')

        let current = Math.max(0, slides.findIndex(s => s.classList.contains('is-active')))

        const play = (next) => {
          if (next === current) return
          // Shortest path around the loop, so 6 -> 1 reads as "forward" rather
          // than as five slides' worth of backwards travel.
          const forward = (next - current + slides.length) % slides.length <= slides.length / 2
          const dir = forward ? 1 : -1
          const outgoing = slides[current]
          const incoming = slides[next]
          current = next

          // overwrite: true - clicking an arrow twice quickly must not leave a
          // half-finished tween fighting the new one.
          gsap.to(outgoing, {
            autoAlpha: 0, x: -dir * DIST, duration: DURATION, ease: EASE, overwrite: true,
          })
          gsap.fromTo(incoming,
            { autoAlpha: 0, x: dir * DIST },
            { autoAlpha: 1, x: 0, duration: DURATION, ease: EASE, overwrite: true },
          )
        }

        classWatcher = new MutationObserver(() => {
          const next = slides.findIndex(s => s.classList.contains('is-active'))
          if (next >= 0) play(next)
        })
        for (const s of slides) classWatcher.observe(s, { attributes: true, attributeFilter: ['class'] })

        return () => {
          if (classWatcher) { classWatcher.disconnect(); classWatcher = null }
          panel.removeAttribute(GATE)
          // Hand the slides back to the CSS that owned them before.
          gsap.set(slides, { clearProps: 'opacity,visibility,transform' })
        }
      })

      return true
    }

    if (attach()) return

    finder = new MutationObserver(() => {
      if (!attach()) return
      finder.disconnect()
      finder = null
    })
    finder.observe(document.body, { childList: true, subtree: true })

    timeoutId = window.setTimeout(() => {
      if (finder) { finder.disconnect(); finder = null }
    }, 10000)

    return () => {
      if (finder) finder.disconnect()
      if (classWatcher) classWatcher.disconnect()
      window.clearTimeout(timeoutId)
    }
  }, { dependencies: [] })

  return null
}
